import { CheckCircle2, CircleHelp, CircleOff, CircleX, ExternalLink, LoaderCircle, Pencil, PlugZap, RefreshCw, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import { ActionInfo, Button, DiagnosticCopy } from '@krp/ui-kit'

import type { ModuleErrorVm, ModuleLevel, ResourceCapabilityVm, ResourceCardActions, ResourceCardVm, ResourceModuleVm } from '../model/types'
import { ActionReason, CommandError } from './ActionReason'
import { QueryState } from '../../../shared/QueryState'
import '../../../shared/platform.css'

/** Права списком: код мелко, состояние словами и значком (не только цветом). */
function CapabilityList({ capabilities }: { capabilities: readonly ResourceCapabilityVm[] }) {
  return (
    <ul>
      {capabilities.map((capability) => (
        <li key={capability.name} className="platform-capability">
          <span className="platform-capability__title">
            {capability.enabled ? <CheckCircle2 size={14} aria-hidden="true" /> : <CircleOff size={14} aria-hidden="true" />}
            {capability.label} — {capability.enabled ? 'включено' : 'выключено'}{' '}
            <code className="platform-capability__code">{capability.name}</code>
          </span>
          {capability.hint !== null && <span className="platform-capability__hint">{capability.hint}</span>}
        </li>
      ))}
    </ul>
  )
}

/** Текст для администратора: модуль, ошибка источника дословно и код. */
function errorReport(title: string, error: ModuleErrorVm): string {
  return [`${title}: ${error.message}`, ...(error.code !== null ? [`Код: ${error.code}`] : [])].join('\n')
}

const LEVEL_ICON: Readonly<Record<ModuleLevel, typeof CheckCircle2>> = {
  ok: CheckCircle2,
  down: CircleX,
  unknown: CircleHelp,
  checking: LoaderCircle,
}

/**
 * Модуль источника (ADR-22): название, состояние словами и значком, ошибка дословно с кодом и «Повторить», права модуля.
 * Состояние показывает только то, что сообщил сервер; не сообщил — так и написано.
 */
function ModuleCard({ module: part, onRetry }: { module: ResourceModuleVm; onRetry: () => void }) {
  const Icon = LEVEL_ICON[part.level]
  const enabled = part.capabilities.filter((capability) => capability.enabled).length
  return (
    <li className="platform-module" data-level={part.level}>
      <h4 className="platform-module__title">{part.title}</h4>
      {part.hint !== null && <p className="platform-capability__hint">{part.hint}</p>}
      <p className="platform-module__state" role="status">
        <Icon size={16} aria-hidden="true" /> {part.stateText}
      </p>
      {part.error !== null && (
        <div className="platform-module__error" role="alert">
          <span>
            Ошибка: {part.error.message}
            {part.error.code !== null ? ` (код ${part.error.code})` : ''}
          </span>
          <DiagnosticCopy text={errorReport(part.title, part.error)} label="Копировать" />
        </div>
      )}
      {part.level === 'down' && (
        <span className="platform-module__retry">
          <Button
            icon={<RefreshCw size={16} aria-hidden="true" />}
            disabled={!part.retry.allowed}
            onClick={onRetry}
            actionId="platform.resource.module-retry"
            disabledReason={part.retry.reason}
          >
            Повторить
          </Button>
          <ActionReason action={part.retry} />
        </span>
      )}
      <details className="platform-module__rights">
        <summary>
          Права модуля: включено {enabled} из {part.capabilities.length}
          <ActionInfo id="platform.resource.module-rights" label="Права модуля" />
        </summary>
        <CapabilityList capabilities={part.capabilities} />
      </details>
    </li>
  )
}

/**
 * Карточка одного ресурса. Окно ресурсное: rid — в адресе и в ключе.
 * `diagnostics` — панель native-пакета вида (профиль, проверка связи службы),
 * которую подставляет приложение по реестру; карточка вида не знает.
 */
export function ResourceCardScreen({
  vm,
  actions,
  diagnostics,
}: {
  vm: ResourceCardVm
  actions: ResourceCardActions
  diagnostics?: ReactNode
}) {
  const resource = vm.resource
  return (
    <section aria-label={resource?.displayName ?? 'Ресурс'} className="platform-resource">
      <QueryState query={vm.query} emptyText="Ресурс не найден" onRetry={actions.reload} />
      {resource !== null && (
        <>
          <dl className="platform-resource-card">
            <dt>Название</dt>
            <dd>{resource.displayName}</dd>
            <dt>Вид</dt>
            <dd>{vm.kindTitle}</dd>
            <dt>Состояние</dt>
            <dd>{resource.enabled ? 'Включён' : 'Выключен'}</dd>
            <dt>Версия контракта</dt>
            <dd>{resource.contractVersion}</dd>
            <dt>Ревизия настроек</dt>
            <dd>{resource.configurationVersion ?? 'сервер не сообщил'}</dd>
            {vm.modules.length === 0 && (
              <>
                <dt>Права</dt>
                <dd>
                  <CapabilityList capabilities={vm.capabilities} />
                </dd>
              </>
            )}
          </dl>

          {vm.modules.length > 0 && (
            <section aria-label="Модули источника" className="platform-modules">
              <h3>Модули</h3>
              <ul className="platform-modules__list">
                {vm.modules.map((part) => (
                  <ModuleCard key={part.id} module={part} onRetry={actions.probe} />
                ))}
              </ul>
            </section>
          )}

          {vm.entries.length > 0 && (
            <div className="platform-resource__entries" role="group" aria-label="Окна ресурса">
              {vm.entries.map((entry) => (
                <span key={entry.window} className="platform-resource__entry">
                  <Button
                    icon={<ExternalLink size={16} aria-hidden="true" />}
                    disabled={!entry.action.allowed}
                    onClick={() => actions.openEntry(entry.window)}
                    actionId="platform.resource.entry"
                    disabledReason={entry.action.reason}
                  >
                    {entry.label}
                  </Button>
                  <ActionReason action={entry.action} />
                </span>
              ))}
            </div>
          )}

          <div className="platform-resource__commands" role="group" aria-label="Действия с ресурсом">
            <Button
              icon={<Pencil size={16} aria-hidden="true" />}
              disabled={!vm.edit.allowed}
              onClick={actions.edit}
              actionId="platform.resource.edit"
              disabledReason={vm.edit.reason}
            >
              Изменить
            </Button>
            <ActionReason action={vm.edit} />
            <Button
              icon={<PlugZap size={16} aria-hidden="true" />}
              disabled={!vm.probe.allowed}
              busy={vm.probeState.busy}
              onClick={actions.probe}
              actionId="platform.resource.probe"
              disabledReason={vm.probe.reason}
            >
              Проверить связь
            </Button>
            <ActionReason action={vm.probe} />
          </div>

          {vm.probeResult !== null && (
            <p className="platform-resource__probe" role="status">
              {vm.probeResult.ok ? (
                <CheckCircle2 size={16} aria-hidden="true" />
              ) : (
                <TriangleAlert size={16} aria-hidden="true" />
              )}{' '}
              {vm.probeResult.ok ? 'Связь есть' : 'Связи нет'}: {vm.probeResult.status}
              {vm.probeResult.code !== null ? ` (код ${vm.probeResult.code})` : ''}
            </p>
          )}
          <CommandError error={vm.probeState.error} />


          <section aria-label="Диагностика службы" className="platform-resource__diagnostics">
            <h3>Диагностика службы</h3>
            {vm.diagnostics.allowed ? diagnostics : <ActionReason action={vm.diagnostics} />}
            {vm.diagnostics.allowed && diagnostics === undefined && <p>Пакет вида не показывает диагностику.</p>}
          </section>
        </>
      )}
      <Button onClick={actions.close} actionId="platform.resource.close">
        Закрыть
      </Button>
    </section>
  )
}
